import { ReactElement, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { ELIF_PAGE_TOPIC_CHAR_LIMIT } from '@lessonPlanner/constants';

import { TextField } from '../components/form-fields';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import DynamicLabelGenerateButton from '../components/molecules/DynamicLabelGenerateButton';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { ELIF_DESCRIPTION } from '../constants/elif';
import { useGeneratePause } from '../context/generate/provider';
import { ElifProperties, PageKey } from '../enums/pageKey';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import ElifSchema from '../schemas/ElifSchema';
import { elifProperties } from '../types/page';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';

const defaultCreditCost = parseInt(
  process.env.NEXT_PUBLIC_DEFAULT_GENERATE_COST,
);

export default function Index(): JSX.Element {
  const defaultValues = {
    topic: getLocalStorageItem(ElifProperties.topic),
  };

  const lessonPlannerForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(ElifSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = lessonPlannerForm;
  const topic = watch('topic');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, topic);

  const formContents = useMemo(
    () => ({
      [ElifProperties.topic]: topic,
    }),
    [topic],
  );

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  async function customFormValidator() {
    await trigger();
  }

  const banner = (
    <GenericBanner content={ELIF_DESCRIPTION} title="Explain Like I'm 5!" />
  );

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);
  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.elif];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  return (
    <MainContent
      banner={banner}
      formContents={formContents as elifProperties}
      pageKey={PageKey.elif}
      setIsLoading={setIsLoading}
    >
      <>
        <TextField
          className="col-span-full"
          errorMessage={!!errors.topic && 'Please enter the Topic'}
          id="topic"
          isMaxLengthReached={topic?.length >= ELIF_PAGE_TOPIC_CHAR_LIMIT}
          label="Topic"
          maxLength={ELIF_PAGE_TOPIC_CHAR_LIMIT}
          name="topic"
          placeholder="Input the topic for this ELI5"
          type="topic"
          value={topic}
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              'topic',
              topic?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              'topic',
              e.target.value,
            )
          }
        />

        <div className="col-span-full">
          <div className="flex flex-col">
            <DynamicLabelGenerateButton
              isRegularDefault
              buttonText={buttonText}
              creditCost={defaultCreditCost}
              handleOnCLick={customFormValidator}
              isDisabled={isGenerateButtonDisabled}
            />
          </div>
        </div>
      </>
    </MainContent>
  );
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
